/**
 * EmbedScreen — full-screen embed viewer for YouTube links.
 * On web: renders an iframe with the YouTube player.
 * On native: opens the URL in the system browser.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Dimensions,
  Platform,
  Linking,
  StatusBar,
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

const BackArrow = ({ color }) => {
  if (!isWeb) return <Text style={{ color, fontSize: 20 }}>←</Text>;
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15,18 9,12 15,6" />
    </svg>
  );
};

const ExternalIcon = ({ color, size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>↗</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15,3 21,3 21,9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
};

export const EmbedScreen = ({ navigation, route }) => {
  const { sheet } = route?.params || {};
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const [screenHeight, setScreenHeight] = useState(SCREEN_HEIGHT);
  const [isDark] = useState(() => isWeb && localStorage?.getItem?.('sheetshed_darkmode') === 'true');

  const theme = isDark ? baseTheme.dark : baseTheme.light;
  const isMobile = screenWidth < 640;

  useEffect(() => {
    if (!isWeb) return;
    const onResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // On native, just open in browser
  useEffect(() => {
    if (!isWeb && sheet?.linkUrl) {
      Linking.openURL(sheet.linkUrl);
      navigation.goBack();
    }
  }, []);

  if (!sheet) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background, justifyContent: 'center', alignItems: 'center' }]}>
        <Text style={{ color: theme.textMuted }}>No link to display.</Text>
      </View>
    );
  }

  const embedSrc = sheet.embedId
    ? `https://www.youtube.com/embed/${sheet.embedId}?autoplay=1&rel=0&modestbranding=1`
    : sheet.linkUrl;

  // Player dimensions — 16:9, full-width minus some padding on desktop
  const maxPlayerWidth = Math.min(screenWidth, 1200);
  const playerWidth = isMobile ? screenWidth : maxPlayerWidth - 80;
  const playerHeight = Math.round(playerWidth * (9 / 16));

  return (
    <View style={[styles.container, { backgroundColor: '#0f0f0f' }]}>
      {/* Top bar */}
      <View style={[styles.topBar, { paddingTop: isMobile ? 44 : 16 }]}>
        <Pressable onPress={() => navigation.goBack()} style={styles.backBtn}>
          <BackArrow color="#fff" />
          <Text style={styles.backLabel} numberOfLines={1}>{sheet.title}</Text>
        </Pressable>

        <Pressable
          onPress={() => isWeb ? window.open(sheet.linkUrl, '_blank') : Linking.openURL(sheet.linkUrl)}
          style={styles.openBtn}
        >
          <ExternalIcon color="rgba(255,255,255,0.7)" size={18} />
        </Pressable>
      </View>

      {/* Player area */}
      <View style={[styles.playerWrap, { flex: 1, justifyContent: 'center', alignItems: 'center' }]}>
        {isWeb ? (
          <iframe
            src={embedSrc}
            width={playerWidth}
            height={Math.max(playerHeight, isMobile ? Math.round(screenHeight * 0.42) : 360)}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ borderRadius: isMobile ? 0 : 12, display: 'block' }}
            title={sheet.title}
          />
        ) : (
          <View style={{ alignItems: 'center', padding: 32 }}>
            <Text style={{ color: '#fff', fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
              Opening in your browser…
            </Text>
          </View>
        )}
      </View>

      {/* Footer info */}
      {sheet.notes ? (
        <View style={styles.notesBar}>
          <Text style={styles.notesText} numberOfLines={2}>{sheet.notes}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
    backgroundColor: '#111',
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    marginRight: 8,
  },
  backLabel: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    flexShrink: 1,
  },
  openBtn: {
    padding: 8,
  },
  playerWrap: {
    backgroundColor: '#000',
  },
  notesBar: {
    backgroundColor: '#111',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  notesText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13,
  },
});

export default EmbedScreen;
